// src/components/agents/agent-runs-page.tsx
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// `SearchInput` (17), `Segmented` (09), `EmptyState` (76), `Banner` (62),
// `SkeletonCard` (69), `Figure` (100), `ModelMark` (98) and `ExpandedPanel`
// (113). `data-kit` on the root.
//
// What did NOT move: `agentsApi.allRuns`, `agentsApi.getRun`, the lazy detail
// fetch, the filter and the search predicate.
//
// Four things the swap fixed, and each is a rule this repo already had:
//
//   * The score pill CHOSE ITS OWN COLOUR from three thresholds — 75 and 50 —
//     that nothing in the specification states. A score is a reading, not a
//     judgement, so it is a `Figure` on the client plane and the colour is
//     gone with the thresholds. UPM-T18.
//   * `{n}/100` was a denominator typed beside a number. `Figure` prints the
//     reading and nothing else.
//   * `StatusBadge` printed the RAW status string ("complete") beside a colour
//     chosen from that string. Label and tone now come from one map.
//   * Seven hardcoded hexes and six `rgba()` tints, none with a dark form.
import { useEffect, useState, useCallback } from "react";
import { History, ChevronDown, ChevronUp, GitBranch } from "lucide-react";
import { AgentRunSummary, AgentRunDetail, agentsApi } from "@/app/services/agents/agents-api";
import { ModuleHeader } from "../shell/module-header";
import {
  Banner, EmptyState, Figure, Icon, SearchInput, Segmented, SkeletonCard,
  Spinner, cx,
} from "../kit";

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

/* A run's outcome. The label and the tone both come from the status, so a chip
   saying "Complete" cannot be red. The raw API string is not printed: the user
   should not have to know that the backend spells it "complete". */
const RUN_STATUS: Record<string, { label: string; tone: string }> = {
  complete: { label: "Complete", tone: "ok" },
  failed:   { label: "Failed",   tone: "error" },
  running:  { label: "Running",  tone: "info" },
};

function StatusChip({ status }: { status: string }) {
  const s = RUN_STATUS[status] ?? { label: "Unknown", tone: "warn" };
  return (
    <span className={cx("k-chip", "k-chip--status")} data-tone={s.tone}>{s.label}</span>
  );
}

function RunRow({ run }: { run: AgentRunSummary }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<AgentRunDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const toggleDetail = async () => {
    if (expanded) { setExpanded(false); return; }
    if (!detail) {
      setLoading(true);
      try { setDetail(await agentsApi.getRun(run.id)); } catch {}
      setLoading(false);
    }
    setExpanded(true);
  };

  return (
    <div className="upm-run">
      <button
        type="button"
        className="upm-run__head"
        onClick={toggleDetail}
        aria-expanded={expanded}
      >
        <span className="upm-run__ident">
          <span className="upm-run__name">{run.agent_name || "Workflow run"}</span>
          <span className="upm-run__meta">
            <span>{run.brand_name}</span>
            {(run.models_used || []).map((m) => (
              <span key={m} className="k-chip" data-tone="neutral">{m}</span>
            ))}
          </span>
        </span>

        {/* A score is a reading on the client plane. It carried a colour
            chosen from two invented thresholds; see the header note. */}
        <span className="upm-run__score">
          <Figure value={run.perception_score ?? null} plane="client" unit="" size="sm" />
        </span>

        <StatusChip status={run.status} />

        <span className="upm-run__when">{formatDate(run.created_at)}</span>

        <span className="upm-run__chevron">
          {loading
            ? <Spinner label="Loading run" />
            : <Icon icon={expanded ? ChevronUp : ChevronDown} size="md" />}
        </span>
      </button>

      {expanded && detail && (
        <div className="upm-run__detail">
          <section className="upm-run__block">
            <span className="k-meta">Prompt used</span>
            <p className="upm-run__quote">{detail.prompt_used}</p>
          </section>

          {detail.aggregated_results?.consensus_summary && (
            <section className="upm-run__block">
              <span className="k-meta">Summary</span>
              <p className="upm-run__body">{detail.aggregated_results.consensus_summary}</p>
            </section>
          )}

          {(detail.aggregated_results?.key_findings || []).length > 0 && (
            <section className="upm-run__block">
              <span className="k-meta">Key findings</span>
              <ol className="upm-run__findings">
                {detail.aggregated_results.key_findings.slice(0, 4).map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ol>
            </section>
          )}

          {Object.keys(detail.aggregated_results?.model_scores || {}).length > 0 && (
            <section className="upm-run__block">
              <span className="k-meta">Model scores</span>
              <div className="upm-run__scores">
                {Object.entries(detail.aggregated_results.model_scores).map(([model, score]) => (
                  <div className="upm-run__score-cell" key={model}>
                    {/* A per-model score is one model's reading of the client,
                        not a competitor's reading, so it stays on the client
                        plane. Moving it to the competitor plane to make it
                        quieter is exactly the move F4 forbids. */}
                    <Figure value={Number(score)} plane="client" unit="" size="md" />
                    <span className="k-meta">{model}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

interface AgentRunsPageProps {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (c: boolean) => void;
  activeBrandName?: string;
}

export function AgentRunsPage({
  isSidebarCollapsed: _s,
  onSidebarToggle: _t,
  activeBrandName,
}: AgentRunsPageProps) {
  const [runs, setRuns] = useState<AgentRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "complete" | "failed">("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRuns(await agentsApi.allRuns({ limit: 200, brandName: activeBrandName }));
    } catch (e: any) {
      setError(e.message || "Failed to load runs");
    } finally {
      setLoading(false);
    }
  }, [activeBrandName]);

  useEffect(() => { load(); }, [load]);

  const filtered = runs.filter((r) => {
    const matchSearch =
      !search ||
      r.agent_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.brand_name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || r.status === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div className="upm-page" data-kit>
      <ModuleHeader
        page="Workflow history"
        icon={History}
        compact
        title="Workflow history"
        description={
          activeBrandName
            ? `Past workflow runs for ${activeBrandName}, ${runs.length} total.`
            : `All past workflow runs across every workflow, ${runs.length} total.`
        }
        action={{ label: "Refresh", variant: "secondary", onClick: load }}
      />

      <div className="shell-page upm-page__body">
        <div className="upm-page__filter">
          <SearchInput
            className="upm-page__search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch("")}
            placeholder="Search by workflow name or brand"
            aria-label="Search runs"
          />
          <Segmented
            label="Run status"
            value={filter}
            onChange={(v) => setFilter(v as typeof filter)}
            options={[
              { value: "all", label: "All" },
              { value: "complete", label: "Complete" },
              { value: "failed", label: "Failed" },
            ]}
          />
        </div>

        {loading && <SkeletonCard />}

        {error && !loading && <Banner tone="error">{error}</Banner>}

        {!loading && !error && filtered.length === 0 && (
          <EmptyState
            icon={GitBranch}
            title={
              search || filter !== "all"
                ? "No runs match your filter"
                : activeBrandName
                ? `No runs yet for ${activeBrandName}`
                : "No runs yet"
            }
            description={
              search || filter !== "all"
                ? "Try adjusting your search or filter."
                : "Run a workflow from the AI Workflows page to see history here."
            }
          />
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="upm-run-list">
            {filtered.map((run) => (
              <RunRow key={run.id} run={run} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
